import { Dialog } from "@dash/dialog";
import { FilterList, FlagFilter, SelectFilter, useQsFilters } from "@dash/filters";
import { Button, DateField } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import React from "react";
import { MdCompare, MdCompareArrows, MdRefresh, MdSync } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";

import { ActivePrice } from "@megaron/catalog-contracts";
import { getMegaraxPath, priceListsFiltersSerializer } from "@megaron/megarax-uri";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { Serializers, SerializerValue } from "@megaron/serializers";

const filtersSerializer = priceListsFiltersSerializer;

type PriceListsFilters = SerializerValue<typeof filtersSerializer>;

export const PriceListsPage = () => {
  const client = useClientManager("catalog");
  const docs = useServiceClient("docs");
  const navigate = useNavigate();
  const defaultFilters = React.useMemo<PriceListsFilters>(
    () => ({ priceLists: [], items: [], searchText: "", activeOn: startOfToday() }),
    [],
  );
  const { filters: rawFilters, setFilters } = useQsFilters(filtersSerializer, defaultFilters);
  const filters = { ...defaultFilters, ...rawFilters };
  const selectedPriceLists = filters.priceLists ?? [];
  const selectedItems = filters.items ?? [];

  const setFilter =
    <TKey extends keyof PriceListsFilters>(key: TKey) =>
    (value: PriceListsFilters[TKey]) =>
      setFilters({ ...filters, [key]: value });

  const priceListsQuery = client.priceListsQuery().useQuery({});
  const priceListOptions = React.useMemo(
    () => priceListsQuery.data?.priceLists.map((list) => ({ label: list.name, value: list.name })) ?? [],
    [priceListsQuery.data],
  );
  const pricesQuery = client.pricesQuery().useQuery({
    priceLists: selectedPriceLists.length > 0 ? selectedPriceLists : undefined,
    items: selectedItems.length > 0 ? selectedItems : undefined,
    searchText: filters.searchText?.trim() || undefined,
    activeOn: filters.activeOn,
  });

  return (
    <Page
      css={{
        maxWidth: "1600px",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <PageHeader
        actions={
          <div css={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button
              variant="outline"
              icon={<MdCompareArrows />}
              onClick={() =>
                navigate(getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: "sheetsPriceList" }))
              }
            >
              Sheets cenniki
            </Button>
            <Button
              variant="outline"
              icon={<MdCompareArrows />}
              onClick={() =>
                navigate(getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: "sheetsPrice" }))
              }
            >
              Sheets ceny
            </Button>
            <Button variant="outline" icon={<MdRefresh />} onClick={() => void pricesQuery.refetch()}>
              Odśwież
            </Button>
          </div>
        }
      >
        Cenniki
      </PageHeader>

      <SearchWithPaginationSticky
        onSearchChange={setFilter("searchText")}
        searchValue={filters.searchText ?? ""}
        hidePagination
      />

      <FilterList>
        <SelectFilter
          label="Cennik"
          initiallySelectedValues={selectedPriceLists}
          options={priceListOptions}
          onSelectedChange={(options) => setFilter("priceLists")(options.map((option) => option.value))}
          variant="multi-select"
          isDisabled={priceListsQuery.data === undefined}
        />
        <SelectFilter
          label="Produkt"
          initiallySelectedValues={selectedItems}
          onSelectedChange={(options) => setFilter("items")(options.map((option) => option.value))}
          variant="multi-select"
          optionalNoResultsText="Wpisz id, nazwę albo EAN..."
          search={async (searchText) => {
            const results = await docs.searchItems({ searchText });

            return (
              results.value?.items.map((item) => ({
                label: [item.itemId, item.itemName, item.gtin].filter(Boolean).join(" · "),
                value: item.itemId,
              })) ?? []
            );
          }}
        />
        <ActiveOnFilter value={filters.activeOn} onChange={setFilter("activeOn")} />
      </FilterList>

      <QuerySkeleton query={pricesQuery}>
        {(data) => (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div css={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.875rem" }}>{data.count} pozycji</div>
            <ActivePricesTable prices={data.prices} isLoading={pricesQuery.isFetching} />
          </div>
        )}
      </QuerySkeleton>
    </Page>
  );
};

const ActiveOnFilter = (props: { value?: Date; onChange: (date: Date | undefined) => void }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <FlagFilter
        isActive={!!props.value}
        label={props.value ? `Aktywne: ${formatDate(props.value)}` : "Aktywne dnia"}
        onClick={() => setIsOpen(true)}
        onDelete={props.value ? () => props.onChange(undefined) : undefined}
      />
      {isOpen && (
        <Dialog header="Aktywne dnia" onClose={() => setIsOpen(false)} css={{ width: "360px" }}>
          <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <DateField label="Data" value={props.value ?? null} onChange={props.onChange} />
            <div css={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => setIsOpen(false)}>Zastosuj</Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

const ActivePricesTable = (props: { prices: ActivePrice[]; isLoading: boolean }) => {
  const theme = useTheme();

  const columns: TableHeadColumn<string>[] = [
    { label: "Cennik" },
    { label: "Id" },
    { label: "Nazwa" },
    { label: "EAN" },
    { label: "Wartość" },
    { label: "Waluta" },
    { label: "Od" },
    { label: "Do" },
    { label: "Zaakceptowane przez" },
    { label: "Data akceptacji" },
  ];

  const mutedCell = { color: theme.colors.secondaryText };

  const getCells = (price: ActivePrice): RowCellContent[] => [
    { element: price.priceList },
    { element: price.itemId, cellCss: mutedCell },
    { element: price.itemName ?? "-", cellCss: mutedCell },
    { element: price.gtin ?? "-", cellCss: mutedCell },
    { element: price.value.toString() },
    { element: price.currency ?? "-", cellCss: mutedCell },
    { element: formatDate(price.activeFrom), cellCss: mutedCell },
    { element: price.activeUntilExclusive ? formatDate(price.activeUntilExclusive) : "-", cellCss: mutedCell },
    {
      element: price.modifiedBy,
      cellCss: mutedCell,
    },
    {
      element: price.modifiedAt.toLocaleString(),
      cellCss: mutedCell,
    },
  ];

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table
        columns={columns}
        css={{
          padding: "0 2rem",
          whiteSpace: "nowrap",
        }}
      >
        <TableBody
          isLoading={props.isLoading}
          rows={props.prices.map((price) => ({
            id: price.id,
            cellsContent: getCells(price),
          }))}
        />
      </Table>
      {props.prices.length === 0 && <div css={{ padding: "1rem 2rem", color: theme.colors.primary }}>Brak cen</div>}
    </div>
  );
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const formatDate = (date: Date) => Serializers.dateOnly.serialize(date);
